import React, { useState, useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import { CalendarDays, Clock3 } from 'lucide-react'
import { IMainProduct } from '../types'
import useProductDataStore from '@/context/ProductDataStore'
import { ScrollContainerContext } from '@/components/ScrollToTop'
import Skeleton from '@/components/Skeleton'
import StarRating from '@/components/StarRating'
import ProductReviewsModal from '@/components/reviews/ProductReviewsModal'
import { formatPriceWithSymbol } from '@/utils/priceFormatting'
import { capDisplayRating } from '@/utils/ratingDisplay'
import { formatHolidayDispatchDelay, formatHolidayEndDate } from '@/utils/holidayDispatch'
import type { HolidayInfo } from '@/services/api'

interface ProductInfoProps {
	_product?: IMainProduct
	currentPrice: number,
	currency: string,
	className?: string,
	isLoading?: boolean,
	primaryTextColor?: string,
	accentColor?: string,
	isPreorder?: boolean,
	preorderMessage?: string,
	preorderNoticeVariant?: 'light' | 'dark',
	holidayInfo?: HolidayInfo | null,
	titleOverride?: string,
	hideTitleOnMobile?: boolean,
	descriptionOverride?: string,
	descriptionLink?: {
		to: string,
		text: string,
		className?: string
	},
	ratingOverride?: number,
	reviewCountOverride?: number,
	showReviews?: boolean,
	reviewProductId?: string,
	reviewRegion?: string,
	showFindOutMore?: boolean
}

const ProductInfo: React.FC<ProductInfoProps> = ({
	_product,
	currentPrice,
	currency,
	className,
	isLoading = false,
	primaryTextColor,
	accentColor,
	isPreorder = false,
	preorderMessage,
	preorderNoticeVariant = 'light',
	holidayInfo,
	titleOverride,
	hideTitleOnMobile = false,
	descriptionOverride,
	descriptionLink,
	ratingOverride,
	reviewCountOverride,
	showReviews = true,
	reviewProductId,
	reviewRegion,
	showFindOutMore = true
}) => {
	const { product } = useProductDataStore()
	const [textExpanded, setTextExpanded] = useState<boolean>(false)
	const [isMobile, setIsMobile] = useState<boolean>(false)
	const [isReviewsModalOpen, setIsReviewsModalOpen] = useState(false)
	const { contentRef, navHeight } = useContext(ScrollContainerContext);

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth <= 768)
		}

		checkMobile()
		window.addEventListener('resize', checkMobile)

		return () => window.removeEventListener('resize', checkMobile)
	}, [])

	const productDescription: string = descriptionOverride ?? (isMobile
		? (product?.productDescription?.mobile || '')
		: (product?.productDescription?.desktop || ''))
	const words = productDescription.split(' ')
	const truncatedText = words.slice(0, 20).join(' ') + ' ...'
	const resolvedTitle = titleOverride ?? product?.productTitle
	const displayTitle = resolvedTitle?.trim() || 'Kibu Wired Headphones'
	const displayRating = capDisplayRating(ratingOverride ?? product?.averageCatalogueRating ?? 0)
	const displayReviewCount = reviewCountOverride ?? product?.totalCatalogueReviews ?? 0

	const scrollToInfo = () => {
		const element = document.getElementById("product-bottom-sections")
		if (element && contentRef?.current) {
			const stickyNavHeight = 70
			const totalOffset = navHeight + stickyNavHeight

			const containerRect = contentRef.current.getBoundingClientRect()
			const elementRect = element.getBoundingClientRect()
			const elementPosition = elementRect.top - containerRect.top + contentRef.current.scrollTop
			const offsetPosition = elementPosition - totalOffset

			contentRef.current.scrollTo({
				top: offsetPosition,
				behavior: "smooth"
			})
		}
	}

	return (
		<div className={`flex flex-col justify-start gap-4 mb-2 ${className}`}>
			{isLoading ? (
				<div className="flex flex-col gap-4">
					<Skeleton className={`h-12 w-3/4 ${hideTitleOnMobile ? 'hidden md:block' : ''}`} />
					<div className={hideTitleOnMobile ? 'flex items-center justify-between gap-4 md:flex-col md:items-start' : 'flex flex-col gap-4'}>
						<Skeleton className={`h-10 w-32 ${hideTitleOnMobile ? 'order-last md:order-first' : ''}`} />
						<div className="flex flex-row gap-1">
							{[1, 2, 3, 4, 5].map((_, index) => (
								<Skeleton key={index} className="w-6 h-6 rounded-md" />
							))}
							<Skeleton className="h-6 w-20 ml-2" />
						</div>
					</div>
					<div className="flex flex-col gap-2">
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-5/6" />
						<Skeleton className="h-4 w-4/6" />
					</div>
					<Skeleton className="h-10 w-32 mt-2" />
				</div>
			) : (
				<>
					<div className={hideTitleOnMobile ? 'flex items-center justify-between gap-4 md:flex-col md:items-start' : 'flex flex-col gap-4'}>
						<div className={`flex items-center justify-center md:flex-col md:items-start gap-3 ${hideTitleOnMobile ? `order-last shrink-0 md:order-first md:ml-0 ${showReviews && displayReviewCount > 0 ? 'ml-auto' : ''}` : ''}`}>
							<h1 className={`leading-none text-4xl md:text-5xl ${hideTitleOnMobile ? 'sr-only md:not-sr-only' : ''} ${primaryTextColor || ''}`}>{displayTitle}</h1>
							<h2 className={`leading-none text-3xl ${accentColor || 'text-orange'}`}>{formatPriceWithSymbol(currency, currentPrice)}</h2>
						</div>
						{showReviews && displayReviewCount > 0 && (
							<button
								type="button"
								className="flex w-fit flex-row items-center gap-2 rounded-md text-left transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-orange focus:ring-offset-2"
								onClick={(e) => {
									e.stopPropagation()
									if (reviewProductId) {
										setIsReviewsModalOpen(true)
									}
								}}
								aria-label={`Read ${displayReviewCount} reviews`}
							>
								<StarRating rating={displayRating} size="md" />
								<div className={`text-sm underline decoration-current underline-offset-4 ${primaryTextColor || ''}`}>{displayReviewCount} Reviews</div>
							</button>
						)}
					</div>
						<div className={`hidden md:block ${primaryTextColor || ''}`}>
							{productDescription}
							{descriptionLink && (
								<>
									{' '}
									<Link
										to={descriptionLink.to}
										className={descriptionLink.className || 'underline'}
									>
										{descriptionLink.text}
									</Link>
								</>
							)}
						</div>
					{/* {showFindOutMore && (
						<button
							className="flex items-center p-2 w-max justify-center gap-1 w-min-content rounded-md font-headline text-kibuBlue leading-none
								transition-all ease-kibu duration-200 items-center cursor-pointer border border-2 border-kibuBlue
								hover:bg-kibuBlue hover:rounded-lg hover:brightness-105 hover:border-black/10 hover:scale-[1.02] hover:text-white"
							onClick={() => scrollToInfo()}>
							<ArrowDown className='h-4 w-4' />
							Find out more
						</button>
					)} */}
					{holidayInfo?.active && holidayInfo?.message && (
						<div className={`mt-2 rounded-lg px-4 py-3 text-sm ${preorderNoticeVariant === 'dark'
							? 'border border-white/30 bg-white/10 text-white'
							: 'border border-[#c4b5fd]/40 bg-[#c4b5fd]/10 text-black'
							}`}>
							<div className="flex items-center gap-2 font-medium">
								<CalendarDays className="h-4 w-4" aria-hidden="true" />
								Holiday dispatch update
							</div>
							<p className="mt-2 leading-relaxed opacity-80">{holidayInfo.message}</p>
							<div className={`mt-4 grid grid-cols-2 overflow-hidden rounded-md border ${preorderNoticeVariant === 'dark'
								? 'divide-x divide-white/20 border-white/20'
								: 'divide-x divide-[#a78bfa]/30 border-[#a78bfa]/30'
								}`}>
								<div className="p-3">
									<div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide opacity-70">
										<CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
										Dispatch resumes
									</div>
									<p className="mt-1 font-medium">After {formatHolidayEndDate(holidayInfo.endDate)}</p>
								</div>
								<div className="p-3">
									<div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide opacity-70">
										<Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
										Extra dispatch time
									</div>
									<p className="mt-1 font-medium">{formatHolidayDispatchDelay(holidayInfo.remainingDays)}</p>
								</div>
							</div>
						</div>
					)}
					{isPreorder && preorderMessage && (
						<div className={`mt-2 rounded-lg px-4 py-3 text-sm ${preorderNoticeVariant === 'dark'
							? 'border border-white/30 bg-white/10 text-white'
							: 'border border-specialYellow/40 bg-specialYellow/10 text-black'
							}`}>
							{preorderMessage}
						</div>
					)}
					{isReviewsModalOpen && reviewProductId && (
						<ProductReviewsModal
							productId={reviewProductId}
							region={reviewRegion}
							onClose={() => setIsReviewsModalOpen(false)}
						/>
					)}
				</>
			)}
		</div>
	);
};

export default ProductInfo;
